/**
 * Voice page. Two layouts:
 *
 *   - Narrow (< 900px / phone): single column, players online stacked
 *     below the PTT card.
 *   - Wide (>= 900px / desktop): two columns - players online pinned on
 *     the left as a sticky sidebar, voice controls on the right.
 *
 * Transmission modes:
 *   - PTT (default): hold the on-screen button or the configured keyboard
 *     hotkey to talk.
 *   - Open mic: VAD with peak-amplitude gate decides which frames go out;
 *     button acts as a local mute toggle. Threshold slider lets the user
 *     tune the gate; a peak meter renders the live mic level so they can
 *     calibrate against their own voice.
 *
 * Parity cards (Desktop-equivalent surfaces on the web client):
 *   - Self diagnostics (packets sent, kbps, voice state)
 *   - Audio devices (mic + output)
 *   - Browser noise suppression toggle
 *   - Bug report form (POSTs to /api/bug-reports)
 *   - Test bridge connection
 *   - Moderation clip capture (last 30s as ZIP of per-speaker WAVs)
 *   - Text-to-speech preview (local only; see voice/tts.ts caveat)
 */

import { fetchHealth, refreshSession, submitBugReport, type BugReportCategory } from '../api.ts'
import {
  DEFAULT_MASTER_GAIN,
  DEFAULT_NOISE_GATE_THRESHOLD,
  DEFAULT_NOISE_SUPPRESSION,
  getLinkedAgid,
  getMasterGain,
  getMicDeviceId,
  getMutedAgids,
  getNoiseGateThreshold,
  getNoiseSuppression,
  getPttKey,
  getRefreshToken,
  getServerId,
  getSpeakerDeviceId,
  getTtsEnabled,
  getTtsHotkey,
  getUserGains,
  getVoiceMode,
  MAX_MASTER_GAIN,
  MAX_USER_GAIN,
  setMasterGain,
  setMicDeviceId,
  setMutedAgids,
  setNoiseGateThreshold,
  setNoiseSuppression,
  setPttKey,
  setSpeakerDeviceId,
  setTtsEnabled,
  setTtsHotkey,
  setUserGain,
  setVoiceMode,
  unlink,
  type VoiceMode,
} from '../auth.ts'
import { AudioEngine, dbfsToLinear, linearToDbfs, probeAudioSupport, type AudioDiagnostics, type AudioStats, type ClipBufferSnapshot, type NegotiatedOpusConfig } from '../voice/audio.ts'
import { buildClipZip, clipZipFilename, downloadBlob } from '../voice/clipExport.ts'
import { VoiceConnection, type ConnectionState } from '../voice/connection.ts'
import { VoiceRtcConnection } from '../voice/connectionRtc.ts'
import { runWebDiagnostics, type DiagReport } from '../voice/diagnostics.ts'
import { audioContextSinkIdSupported, listAudioInputs, listAudioOutputs, type AudioDevice } from '../voice/devices.ts'
import { SleepGuard } from '../voice/sleep.ts'
import { SnapshotConnection, type RosterEntry } from '../voice/snapshot.ts'
import { speak, ttsAvailable } from '../voice/tts.ts'
import { el, mount } from '../ui/dom.ts'

export interface VoicePageOptions {
  readonly root: HTMLElement
  readonly onUnlinked: () => void
}

type BridgeProbeResult =
  | { kind: 'idle' }
  | { kind: 'running'; text: string }
  | { kind: 'ok'; text: string }
  | { kind: 'fail'; text: string }

type DiagRunState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'done'; report: DiagReport }

type BugReportStatus =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'ok'; id: number }
  | { kind: 'fail'; message: string }

interface BugReportDraft {
  category: BugReportCategory
  summary: string
  details: string
  contact: string
  open: boolean
  status: BugReportStatus
}

interface TtsState {
  open: boolean
  text: string
  speaking: boolean
  lastError: string | null
}

interface State {
  connected: boolean
  pttDown: boolean
  /** Local mute toggle for open-mic mode. PTT mode ignores this. */
  openMicMuted: boolean
  connectionState: ConnectionState | null
  status: string
  statusKind: '' | 'ok' | 'error' | 'warn'
  fatal: FatalInfo | null
  /**
   * Mic-permission denial is sticky on iOS Safari (and most other browsers
   * once the user has said "Don't Allow" or revoked): subsequent
   * getUserMedia calls reject silently without a fresh popup. When we trip
   * NotAllowedError we set this so the UI can render a clear remediation
   * card with platform-aware instructions and a Retry button distinct from
   * the regular Connect button. Cleared on the next successful connect or
   * disconnect.
   */
  micPermissionBlocked: boolean
  /**
   * Live Permissions API state for the microphone, refreshed each time we
   * surface the recovery card. Lets us tell the user EXACTLY what their
   * browser thinks ("denied" vs "prompt" vs "granted"), so a user who has
   * reset permission in Chrome settings but still can't get a popup knows
   * to look at the OS-level permission instead of going in circles.
   */
  micPermissionState: 'granted' | 'denied' | 'prompt' | 'unknown'
  roster: ReadonlyArray<RosterEntry>
  agidToLinkId: Map<string, string>
  audibleAgids: Set<string>
  talkingAgids: Set<string>
  masterGain: number
  userGains: Record<string, number>
  mutedAgids: Set<string>
  selfAgid: string | null
  mode: VoiceMode
  noiseGateThreshold: number
  /** Latest mic peak (0..1). Updated by the same 10 Hz poll that drives talking. */
  micPeak: number
  pttKey: string | null
  noiseSuppression: boolean
  micDeviceId: string | null
  speakerDeviceId: string | null
  micDevices: ReadonlyArray<AudioDevice>
  speakerDevices: ReadonlyArray<AudioDevice>
  outputSinkSupported: boolean
  stats: AudioStats
  probe: BridgeProbeResult
  diag: DiagRunState
  bug: BugReportDraft
  ttsEnabled: boolean
  ttsHotkey: string | null
  tts: TtsState
}

function initialState(): State {
  return {
    connected: false,
    pttDown: false,
    openMicMuted: false,
    connectionState: null,
    status: 'Ready to connect.',
    statusKind: '',
    fatal: null,
    micPermissionBlocked: false,
    micPermissionState: 'unknown',
    roster: [],
    agidToLinkId: new Map(),
    audibleAgids: new Set(),
    talkingAgids: new Set(),
    masterGain: getMasterGain(),
    userGains: getUserGains(),
    mutedAgids: new Set(getMutedAgids()),
    selfAgid: getLinkedAgid(),
    mode: getVoiceMode(),
    noiseGateThreshold: getNoiseGateThreshold(),
    micPeak: 0,
    pttKey: getPttKey(),
    noiseSuppression: getNoiseSuppression(),
    micDeviceId: getMicDeviceId(),
    speakerDeviceId: getSpeakerDeviceId(),
    micDevices: [],
    speakerDevices: [],
    outputSinkSupported: audioContextSinkIdSupported(),
    stats: { packetsSent: 0, bytesSent: 0, kbpsRecent: 0, micPeak: 0 },
    probe: { kind: 'idle' },
    diag: { kind: 'idle' },
    bug: {
      category: 'audio',
      summary: '',
      details: '',
      contact: '',
      open: false,
      status: { kind: 'idle' },
    },
    ttsEnabled: getTtsEnabled(),
    ttsHotkey: getTtsHotkey(),
    tts: { open: false, text: '', speaking: false, lastError: null },
  }
}

export function renderVoicePage(opts: VoicePageOptions): void {
  const state = initialState()

  let engine: AudioEngine | null = null
  // The voice connection is either the legacy WebSocket-backed
  // VoiceConnection or the new WebRTC DataChannel VoiceRtcConnection. Both
  // expose the same shape (start/stop/sendOpus/reconnectNow) so the rest
  // of this file doesn't need to care which one is active. We try RTC
  // first; if negotiation fails (strict NAT, ICE blocked, etc.) we swap
  // to the WS path silently. Query param `?voice=ws` forces the legacy
  // path for debugging.
  let connection: VoiceConnection | VoiceRtcConnection | null = null
  let snapshots: SnapshotConnection | null = null
  let pollTimer: ReturnType<typeof setInterval> | null = null
  let keyHandler: ((e: KeyboardEvent) => void) | null = null
  let keyUpHandler: ((e: KeyboardEvent) => void) | null = null
  let sleep: SleepGuard | null = null
  let deviceChangeListener: (() => void) | null = null
  // Resolved once at startup. null = no Opus config negotiable on this
  // browser (typically Safari < 18.4 / iOS Safari < 18.4 / Firefox < 130).
  // Connect handler bails with a clear message when null.
  let opusConfig: NegotiatedOpusConfig | null = null

  const rerender = (): void => mount(opts.root, view(state, handlers))

  const pushMutes = (): void => {
    if (snapshots === null) return
    const linkIds: string[] = []
    for (const agid of state.mutedAgids) {
      const linkId = state.agidToLinkId.get(agid)
      if (linkId !== undefined) linkIds.push(linkId)
    }
    snapshots.setPersonalMutes(linkIds)
  }

  /** Drive engine.muted from current mode + button state. */
  const applyTxState = (): void => {
    if (engine === null) return
    if (state.mode === 'ptt') {
      engine.setMuted(!state.pttDown)
    } else {
      engine.setMuted(state.openMicMuted)
    }
  }

  /** Capture-phase keyboard listeners for PTT + TTS summon. Active only while
   *  the user is connected so a stray key in the bug-report textarea doesn't
   *  unexpectedly transmit. */
  const ensureKeyListeners = (): void => {
    if (keyHandler !== null) return
    const handler = (e: KeyboardEvent): void => {
      if (e.repeat) return
      if (isTypingTarget(e.target)) return
      const key = e.key.toUpperCase()
      if (state.pttKey !== null && key === state.pttKey && state.mode === 'ptt' && !state.pttDown) {
        e.preventDefault()
        state.pttDown = true
        applyTxState()
        rerender()
      }
      if (state.ttsEnabled && state.ttsHotkey !== null && key === state.ttsHotkey && !state.tts.open) {
        e.preventDefault()
        state.tts.open = true
        state.tts.text = ''
        state.tts.lastError = null
        rerender()
      }
    }
    const up = (e: KeyboardEvent): void => {
      const key = e.key.toUpperCase()
      if (state.pttKey !== null && key === state.pttKey && state.mode === 'ptt' && state.pttDown) {
        e.preventDefault()
        state.pttDown = false
        applyTxState()
        rerender()
      }
    }
    document.addEventListener('keydown', handler, true)
    document.addEventListener('keyup', up, true)
    keyHandler = handler
    keyUpHandler = up
  }

  const removeKeyListeners = (): void => {
    if (keyHandler !== null) document.removeEventListener('keydown', keyHandler, true)
    if (keyUpHandler !== null) document.removeEventListener('keyup', keyUpHandler, true)
    keyHandler = null
    keyUpHandler = null
  }

  /** Populate the device dropdowns. enumerateDevices reports blank labels
   *  until the page has been granted mic permission once, so we re-call this
   *  after a successful startMic. */
  const refreshDevices = async (): Promise<void> => {
    const [mics, outs] = await Promise.all([listAudioInputs(), listAudioOutputs()])
    state.micDevices = mics
    state.speakerDevices = outs
    rerender()
  }

  /**
   * Called by SleepGuard when the page transitions from hidden to visible.
   * Mobile browsers auto-suspend AudioContext on screen lock, so even a
   * successful WS reconnect plays no sound until we explicitly resume.
   * The OS may also have ended the mic track during a long lock; if so,
   * we rebuild it. Finally we kick the WS into an immediate reconnect
   * instead of waiting out the exponential backoff.
   */
  const onPageResumed = async (): Promise<void> => {
    if (engine === null) return
    state.status = 'Resuming after wake...'
    state.statusKind = 'warn'
    rerender()
    try {
      const { micTrackEnded } = await engine.resumeAfterWake()
      if (micTrackEnded) {
        await engine.restartMic({
          deviceId: state.micDeviceId,
          noiseSuppression: state.noiseSuppression,
        })
        applyTxState()
      }
    } catch (err) {
      console.warn('audio resume failed', err)
    }
    // Only force a reconnect if the voice link is actually down. Firing this on
    // every visibilitychange (e.g. alt-tabbing to a fullscreen game and back)
    // tore down a perfectly healthy connection, which collided with the bridge's
    // duplicate-session guard and left the client flapping with no audio. A live
    // datachannel/WS survives a tab switch on its own; genuine drops still
    // recover because the connection's own close handler schedules a reconnect.
    if (!state.connected) {
      connection?.reconnectNow()
    }
  }

  const handlers = {
    connect: async (): Promise<void> => {
      const serverId = getServerId()
      if (serverId === null) {
        state.status = 'No server selected, re-link first.'
        state.statusKind = 'error'
        rerender()
        return
      }
      state.status = 'Requesting mic permission...'
      state.statusKind = ''
      rerender()

      if (opusConfig === null) {
        state.status = "This browser can't encode Opus audio. Update Safari to 18.4+ (iOS 18.4+) or use Chrome / Firefox."
        state.statusKind = 'error'
        rerender()
        return
      }
      const negotiated = opusConfig
      const newEngine = new AudioEngine({
        onOpusPacket: (opus) => connection?.sendOpus(opus),
        opusConfig: negotiated,
        onCodecError: (where, err) => {
          const message = err instanceof Error ? err.message : String(err)
          state.fatal = buildRuntimeAudioError(where, message)
          rerender()
        },
      })
      try {
        await newEngine.init()
        await newEngine.startMic({
          deviceId: state.micDeviceId,
          noiseSuppression: state.noiseSuppression,
        })
        if (state.outputSinkSupported && state.speakerDeviceId !== null) {
          await newEngine.setOutputDeviceId(state.speakerDeviceId)
        }
        // Connect succeeded - clear any previous denial banner. Sticky-state
        // hygiene: a user who fixes their permission in Safari Settings and
        // hits Retry needs the banner to disappear once we actually got the
        // stream.
        state.micPermissionBlocked = false
      } catch (err) {
        // Detect mic-permission denial separately from other audio init
        // failures (codec mismatch, device gone). On iOS Safari, once the
        // user has tapped "Don't Allow", subsequent getUserMedia calls
        // reject with NotAllowedError silently - no popup. The user has to
        // manually re-enable in Safari and then hit Retry, so we render a
        // distinct remediation card instead of the generic error line.
        //
        // Detection is intentionally permissive: Chrome throws a real
        // DOMException with NotAllowedError, Safari historically throws
        // PermissionDeniedError, Android Chrome wrappers have been seen
        // forwarding a plain Error with message "Permission denied" or
        // "permission denied". Match all of those plus a Permissions API
        // fallback in case the error didn't carry a recognisable shape.
        const isPermDenied = await detectPermissionDenied(err)
        // Always show the recovery card on any init failure. Trying to
        // distinguish "permission" from "codec" / "device" failures via
        // error-shape sniffing has proven brittle across browser versions;
        // a false-positive "your mic is blocked" card for a non-permission
        // failure is harmless (the Retry button just re-runs connect and
        // surfaces the same error again), whereas a false-negative leaves
        // the user with no recovery action. Always-on is the correct
        // bias for this UI.
        state.micPermissionBlocked = true
        // Refresh the Permissions API state so the card can tell the user
        // EXACTLY what the browser thinks - resolves the "I cleared
        // permission but still no popup" confusion by showing them the
        // browser's view of reality.
        state.micPermissionState = await readMicPermissionState()
        // Diagnostic: keep the error shape in the status line so future
        // tester reports tell us exactly what fired, not just a guess.
        const errObj = err as { name?: unknown; message?: unknown }
        const eName = typeof errObj?.name === 'string' && errObj.name.length > 0 ? errObj.name : 'Error'
        const eMsg = typeof errObj?.message === 'string' ? errObj.message : String(err)
        state.status = isPermDenied
          ? 'Microphone access blocked.'
          : `Audio init failed: ${eName}: ${eMsg}`
        state.statusKind = 'error'
        try { await newEngine.dispose() } catch { /* ignore */ }
        rerender()
        return
      }
      engine = newEngine
      newEngine.setMasterGain(state.masterGain)
      newEngine.setMode(state.mode)
      newEngine.setVadThreshold(state.noiseGateThreshold)
      for (const [agid, gain] of Object.entries(state.userGains)) {
        newEngine.setSenderUserGain(agid, gain)
      }
      for (const agid of state.mutedAgids) {
        newEngine.setSenderMuted(agid, true)
      }
      applyTxState()
      ensureKeyListeners()
      void refreshDevices()
      // Plug / unplug / Bluetooth toggle should refresh the dropdown list
      // without the user having to disconnect first.
      if (navigator.mediaDevices !== undefined) {
        deviceChangeListener = (): void => { void refreshDevices() }
        navigator.mediaDevices.addEventListener('devicechange', deviceChangeListener)
      }
      // The SleepGuard was acquired at page-load (see bottom of
      // renderVoicePage); the connect button click is the user gesture
      // that lets the silent-keepalive audio start. Without this, autoplay
      // policy may have blocked the keepalive on initial page load.
      sleep?.primeKeepaliveAfterUserGesture()

      // Transport selection. Default is the WebSocket path: it rides the
      // already-open HTTP/WS port (8786), needs no ICE/STUN negotiation, and
      // works through default-deny firewalls (NA runs UFW active, which blocks
      // werift's ephemeral ICE UDP ports - RTC there never completes, leaving
      // web users "connected" but silent for 8s before falling back). The WS
      // path routes through the exact same proximity router the desktop UDP
      // client uses, so audio just works everywhere. `?voice=rtc` opts back
      // into the WebRTC DataChannel path for testing on networks where ICE
      // succeeds.
      const forcedTransport = new URLSearchParams(window.location.search).get('voice')
      const preferRtc = forcedTransport === 'rtc'

      // One stable id for this connect, shared by the WS path and the RTC path.
      // The bridge uses it to treat our OWN reconnects (flaky mobile networks
      // especially) as a clean replace instead of a 4006 rejection, which is
      // what caused the connect/reconnect flipping.
      const clientInstanceId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`

      const onStateHandler = (cs: ConnectionState): void => {
        state.connectionState = cs
        state.connected = cs.kind === 'connected'
        state.status = formatConnState(cs)
        state.statusKind = cs.kind === 'connected' ? 'ok' : cs.kind === 'reconnecting' ? 'warn' : ''
        rerender()
      }
      const onAuthFailedHandler = (): void => { unlink(); opts.onUnlinked() }

      const startWsConnection = (): void => {
        const conn = new VoiceConnection({
          serverId,
          clientInstanceId,
          engine: newEngine,
          onState: onStateHandler,
          onAuthFailed: onAuthFailedHandler,
        })
        connection = conn
        void conn.start()
      }

      if (preferRtc) {
        const rtcConn = new VoiceRtcConnection({
          serverId,
          clientInstanceId,
          engine: newEngine,
          onState: onStateHandler,
          onAuthFailed: onAuthFailedHandler,
          onRtcUnavailable: (reason: string) => {
            // RTC couldn't negotiate (strict NAT, ICE blocked, bridge
            // didn't respond in time). Stop the RTC attempt cleanly and
            // fall back to the legacy WS path so the user still gets
            // voice. Re-tries of RTC on the next page load are fine.
            console.warn('voice rtc unavailable, falling back to ws:', reason)
            if (connection !== null) {
              connection.stop()
            }
            startWsConnection()
          },
        })
        connection = rtcConn
        void rtcConn.start()
      } else {
        startWsConnection()
      }

      const snap = new SnapshotConnection({
        serverId,
        onHello: (msg) => { state.selfAgid = msg.agid },
        onSnapshot: (frame) => {
          state.roster = frame.roster
          const audible = new Set<string>()
          const gains = new Map<string, {
            gainL: number
            gainR: number
            localX?: number
            localY?: number
            localZ?: number
          }>()
          for (const a of frame.audio) {
            audible.add(a.agid)
            // Forward the listener-local position when the bridge sent it
            // (new wire format). The engine's PannerNode HRTF path uses
            // those for 3D placement; old bridges that don't send them fall
            // back to amplitude-pan via gainL/gainR.
            const entry: {
              gainL: number
              gainR: number
              localX?: number
              localY?: number
              localZ?: number
            } = { gainL: a.gainL, gainR: a.gainR }
            if (a.localX !== undefined) entry.localX = a.localX
            if (a.localY !== undefined) entry.localY = a.localY
            if (a.localZ !== undefined) entry.localZ = a.localZ
            gains.set(a.agid, entry)
          }
          state.audibleAgids = audible
          engine?.setSpatialGains(gains)
          // Keep clip-export labels in sync so saved WAVs are named by player.
          if (engine !== null) {
            for (const r of frame.roster) engine.setSpeakerLabel(r.agid, r.name)
          }
          rerender()
        },
        onVoiceSession: (evt) => {
          if (evt.active) {
            state.agidToLinkId.set(evt.agid, evt.linkId)
          } else {
            state.agidToLinkId.delete(evt.agid)
          }
          engine?.onVoiceSession(evt.sessionId, evt.agid, evt.active)
          if (state.mutedAgids.has(evt.agid)) pushMutes()
        },
        onAuthFailed: () => { unlink(); opts.onUnlinked() },
        onState: () => { /* not surfaced */ },
      })
      snapshots = snap
      void snap.start()

      // Single 10 Hz poll that drives talking, mic peak, and the
      // self-diagnostics card. We deliberately do NOT rerender on every
      // packet (~50Hz of mic frames during voice) - the smoothed
      // kbpsBucket already covers "actively transmitting" at 1 Hz, and
      // per-packet rerenders were churning the DOM enough to race with
      // mobile Safari's native <select> menu open gesture.
      pollTimer = setInterval(() => {
        if (engine === null) return
        const talkingNext = new Set<string>()
        for (const agid of engine.knownSenderAgids()) {
          if (engine.isTalking(agid)) talkingNext.add(agid)
        }
        const stats = engine.getStats()
        const peakNext = stats.micPeak
        const talkingChanged =
          talkingNext.size !== state.talkingAgids.size ||
          [...talkingNext].some((a) => !state.talkingAgids.has(a))
        const peakBucket = Math.round(peakNext * 100)
        const peakChanged = peakBucket !== Math.round(state.micPeak * 100)
        const kbpsBucket = Math.round(stats.kbpsRecent)
        const kbpsChanged = kbpsBucket !== Math.round(state.stats.kbpsRecent)
        if (!talkingChanged && !peakChanged && !kbpsChanged) return
        state.talkingAgids = talkingNext
        state.micPeak = peakNext
        state.stats = stats
        rerender()
      }, 100)
    },
    disconnect: async (): Promise<void> => {
      connection?.stop()
      connection = null
      snapshots?.stop()
      snapshots = null
      if (pollTimer !== null) { clearInterval(pollTimer); pollTimer = null }
      removeKeyListeners()
      if (deviceChangeListener !== null && navigator.mediaDevices !== undefined) {
        navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener)
        deviceChangeListener = null
      }
      // SleepGuard is page-scoped, not connection-scoped: it stays alive
      // across disconnect/reconnect so the screen keeps holding awake
      // while the user is on the voice page. Teardown lives in unlink:.
      if (engine !== null) {
        await engine.dispose()
        engine = null
      }
      state.connected = false
      state.pttDown = false
      state.connectionState = null
      state.status = 'Disconnected.'
      state.statusKind = ''
      state.micPermissionBlocked = false
      state.roster = []
      state.audibleAgids = new Set()
      state.talkingAgids = new Set()
      state.micPeak = 0
      state.stats = { packetsSent: 0, bytesSent: 0, kbpsRecent: 0, micPeak: 0 }
      rerender()
    },
    pttDown: (): void => {
      if (!state.connected || state.mode !== 'ptt') return
      state.pttDown = true
      applyTxState()
      rerender()
    },
    pttUp: (): void => {
      if (state.mode !== 'ptt') return
      state.pttDown = false
      applyTxState()
      rerender()
    },
    toggleOpenMicMute: (): void => {
      if (!state.connected || state.mode !== 'openMic') return
      state.openMicMuted = !state.openMicMuted
      applyTxState()
      rerender()
    },
    setMode: (mode: VoiceMode): void => {
      state.mode = mode
      setVoiceMode(mode)
      engine?.setMode(mode)
      state.pttDown = false
      state.openMicMuted = false
      applyTxState()
      rerender()
    },
    setNoiseGateThresholdDb: (db: number): void => {
      const linear = Math.max(0, Math.min(1, dbfsToLinear(db)))
      state.noiseGateThreshold = linear
      setNoiseGateThreshold(linear)
      engine?.setVadThreshold(linear)
      rerender()
    },
    setMasterGain: (value: number): void => {
      state.masterGain = Math.max(0, Math.min(MAX_MASTER_GAIN, value))
      setMasterGain(state.masterGain)
      engine?.setMasterGain(state.masterGain)
      rerender()
    },
    setUserGain: (agid: string, value: number): void => {
      const clamped = Math.max(0, Math.min(MAX_USER_GAIN, value))
      state.userGains = { ...state.userGains, [agid]: clamped }
      setUserGain(agid, clamped)
      engine?.setSenderUserGain(agid, clamped)
      rerender()
    },
    toggleMute: (agid: string): void => {
      if (state.mutedAgids.has(agid)) state.mutedAgids.delete(agid)
      else state.mutedAgids.add(agid)
      setMutedAgids([...state.mutedAgids])
      engine?.setSenderMuted(agid, state.mutedAgids.has(agid))
      pushMutes()
      rerender()
    },
    setPttKey: (raw: string): void => {
      const trimmed = raw.trim().toUpperCase().slice(0, 1)
      const next = trimmed.length === 1 && /^[A-Z0-9]$/.test(trimmed) ? trimmed : null
      state.pttKey = next
      setPttKey(next)
      rerender()
    },
    setNoiseSuppression: (enabled: boolean): void => {
      state.noiseSuppression = enabled
      setNoiseSuppression(enabled)
      state.status = engine !== null
        ? 'Noise suppression preference saved. Reconnect to apply.'
        : 'Noise suppression preference saved.'
      state.statusKind = ''
      rerender()
    },
    setMicDeviceId: (deviceId: string | null): void => {
      state.micDeviceId = deviceId
      setMicDeviceId(deviceId)
      state.status = engine !== null
        ? 'Mic preference saved. Reconnect to switch input.'
        : 'Mic preference saved.'
      state.statusKind = ''
      rerender()
    },
    setSpeakerDeviceId: async (deviceId: string | null): Promise<void> => {
      state.speakerDeviceId = deviceId
      setSpeakerDeviceId(deviceId)
      if (engine !== null && state.outputSinkSupported) {
        const ok = await engine.setOutputDeviceId(deviceId)
        state.status = ok ? 'Output device switched.' : 'Browser refused output switch.'
        state.statusKind = ok ? 'ok' : 'warn'
      } else if (!state.outputSinkSupported) {
        state.status = 'This browser does not support routing output to a specific device. Use the OS audio panel instead.'
        state.statusKind = 'warn'
      }
      rerender()
    },
    testBridge: async (): Promise<void> => {
      state.probe = { kind: 'running', text: 'Probing /api/health ...' }
      rerender()
      const lines: string[] = []
      try {
        const h = await fetchHealth()
        lines.push(`/api/health: ok (region=${h.region}, uptime=${h.uptimeSeconds}s)`)
      } catch (err) {
        state.probe = { kind: 'fail', text: `health failed: ${err instanceof Error ? err.message : String(err)}` }
        rerender()
        return
      }
      const token = getRefreshToken()
      if (token === null) {
        state.probe = { kind: 'fail', text: lines.concat('refresh: no token (not linked).').join('\n') }
        rerender()
        return
      }
      let sessionToken: string
      try {
        const r = await refreshSession(token)
        sessionToken = r.sessionToken
        lines.push(`/api/session/refresh: ok (expiresAt=${r.expiresAt})`)
      } catch (err) {
        state.probe = { kind: 'fail', text: lines.concat(`refresh failed: ${err instanceof Error ? err.message : String(err)}`).join('\n') }
        rerender()
        return
      }
      const serverId = getServerId()
      if (serverId === null) {
        state.probe = { kind: 'fail', text: lines.concat('ws: no server selected.').join('\n') }
        rerender()
        return
      }
      try {
        await probeWs(sessionToken, serverId)
        lines.push('voice ws: connected + closed cleanly')
        state.probe = { kind: 'ok', text: lines.join('\n') }
      } catch (err) {
        lines.push(`voice ws: ${err instanceof Error ? err.message : String(err)}`)
        state.probe = { kind: 'fail', text: lines.join('\n') }
      }
      rerender()
    },
    runDiagnostics: async (): Promise<void> => {
      state.diag = { kind: 'running' }
      rerender()
      try {
        const report = await runWebDiagnostics({
          connected: state.connected,
          selfAgid: state.selfAgid,
          rosterCount: state.roster.length,
          audibleCount: state.audibleAgids.size,
          selfInRoster: state.selfAgid !== null && state.roster.some((r) => r.agid === state.selfAgid),
        })
        state.diag = { kind: 'done', report }
      } catch (err) {
        // The runner is defensive per-check, so reaching here means something
        // unexpected; surface it as a one-line failed report rather than a blank.
        const detail = err instanceof Error ? err.message : String(err)
        state.diag = {
          kind: 'done',
          report: {
            checks: [{ status: 'fail', title: 'Diagnostics', detail }],
            verdict: `Diagnostics could not complete: ${detail}`,
            logText: `Diagnostics crashed: ${detail}`,
          },
        }
      }
      rerender()
    },
    downloadDiagnostics: (): void => {
      if (state.diag.kind !== 'done') return
      const stamp = new Date().toISOString().replace(/[:.]/g, '-')
      const blob = new Blob([state.diag.report.logText], { type: 'text/plain' })
      downloadBlob(blob, `ashenage-voice-diagnostics-${stamp}.log`)
    },
    captureClip: async (): Promise<void> => {
      if (engine === null) {
        state.status = 'Connect to voice first so there is audio to capture.'
        state.statusKind = 'warn'
        rerender()
        return
      }
      const snap: ClipBufferSnapshot = engine.snapshotClipBuffer()
      if (snap.perSpeaker.size === 0) {
        state.status = 'No audio in the buffer yet. Wait until someone speaks, then try again.'
        state.statusKind = 'warn'
        rerender()
        return
      }
      const blob = buildClipZip(snap, (agid) => engine!.speakerLabel(agid))
      downloadBlob(blob, clipZipFilename(snap))
      state.status = `Saved clip with ${snap.perSpeaker.size} speaker(s).`
      state.statusKind = 'ok'
      rerender()
    },
    openBugReport: (): void => {
      state.bug.open = true
      state.bug.status = { kind: 'idle' }
      rerender()
    },
    closeBugReport: (): void => {
      state.bug.open = false
      rerender()
    },
    setBugField: <K extends 'category' | 'summary' | 'details' | 'contact'>(key: K, value: BugReportDraft[K]): void => {
      // category triggers a rerender (the select needs to reflect choice).
      // Free-text fields keep iOS keyboard alive: skip rerender.
      ;(state.bug as BugReportDraft)[key] = value
      if (key === 'category') rerender()
    },
    submitBug: async (): Promise<void> => {
      const token = getRefreshToken()
      if (token === null) {
        state.bug.status = { kind: 'fail', message: 'You need to be linked first.' }
        rerender()
        return
      }
      const summary = state.bug.summary.trim()
      const details = state.bug.details.trim()
      if (summary.length === 0 || details.length === 0) {
        state.bug.status = { kind: 'fail', message: 'Summary and details are required.' }
        rerender()
        return
      }
      state.bug.status = { kind: 'submitting' }
      rerender()
      try {
        const contact = state.bug.contact.trim()
        const req = {
          refreshToken: token,
          category: state.bug.category,
          summary,
          details,
          clientVersion: 'web/0.1',
          clientOs: navigator.userAgent.slice(0, 60),
          ...(contact.length > 0 ? { contact } : {}),
        }
        const res = await submitBugReport(req)
        state.bug.status = { kind: 'ok', id: res.id }
        state.bug.summary = ''
        state.bug.details = ''
        state.bug.contact = ''
      } catch (err) {
        state.bug.status = { kind: 'fail', message: err instanceof Error ? err.message : 'Submit failed.' }
      }
      rerender()
    },
    setTtsEnabled: (enabled: boolean): void => {
      state.ttsEnabled = enabled
      setTtsEnabled(enabled)
      if (!enabled) state.tts.open = false
      rerender()
    },
    setTtsHotkey: (raw: string): void => {
      const trimmed = raw.trim().toUpperCase().slice(0, 1)
      const next = trimmed.length === 1 && /^[A-Z0-9]$/.test(trimmed) ? trimmed : null
      state.ttsHotkey = next
      setTtsHotkey(next)
      rerender()
    },
    openTts: (): void => {
      state.tts.open = true
      state.tts.text = ''
      state.tts.lastError = null
      rerender()
    },
    closeTts: (): void => {
      state.tts.open = false
      rerender()
    },
    setTtsText: (value: string): void => {
      // Mutated without rerender (textarea preserves its own value).
      state.tts.text = value
    },
    speakTts: async (): Promise<void> => {
      const text = state.tts.text.trim()
      if (text.length === 0) return
      state.tts.speaking = true
      state.tts.lastError = null
      rerender()
      try {
        await speak({ text })
      } catch (err) {
        state.tts.lastError = err instanceof Error ? err.message : String(err)
      } finally {
        state.tts.speaking = false
        rerender()
      }
    },
    unlink: async (): Promise<void> => {
      await handlers.disconnect()
      // Release the page-scoped SleepGuard now that we're navigating away.
      if (sleep !== null) {
        const s = sleep
        sleep = null
        void s.stop()
      }
      unlink()
      opts.onUnlinked()
    },
  }

  // Single-call feature probe: runs the sync constructor check AND the
  // async isConfigSupported probes for each Opus shape we'd accept, then
  // returns one diagnostics report covering everything. We render the
  // exact failures (which constructor was missing, which encoder configs
  // came back unsupported, whether WebAssembly is missing as a Lockdown
  // Mode tell, etc.) rather than guessing. UI surfaces the literal probe
  // results so the user sees what's failing instead of vague hints.
  rerender()
  void refreshDevices()
  void (async (): Promise<void> => {
    const probe = await probeAudioSupport()
    if (probe.config === null) {
      state.fatal = buildBrowserUnsupportedMessage(probe.diagnostics)
      rerender()
      return
    }
    opusConfig = probe.config
  })()

  // Acquire wake lock + silent keepalive as soon as the voice page mounts,
  // NOT only on connect. Stopgap for "voice cuts out when phone screen
  // sleeps" until the native mobile app is shipping. Wake lock keeps the
  // screen on while the user is foreground; silent keepalive marks the
  // page as media-playing so Android Chrome is less aggressive about
  // suspending it in the background.
  //
  // start() returns immediately; both pieces are best-effort and silently
  // degrade on browsers that don't support them (e.g. Firefox lacks Wake
  // Lock at time of writing). User pressing the physical lock button is
  // still respected; no browser API can override that.
  sleep = new SleepGuard({
    onResume: () => { void onPageResumed() },
    enableSilentKeepalive: true,
  })
  void sleep.start()
}

interface FatalInfo {
  /** One-line summary of which check ultimately failed. */
  readonly detail: string
  /**
   * Full feature-probe report, rendered as a table in the card. Null for
   * mid-session runtime errors where the initial probe already succeeded.
   */
  readonly diagnostics: AudioDiagnostics | null
  /** Platform-specific guidance text. */
  readonly platformHint: string
  /**
   * Non-null when we can confidently say the user is in an in-app WebView
   * (Discord, Instagram, Slack, etc.) or a third-party iOS browser. Those
   * environments strip WebCodecs even on iOS 18.4+.
   */
  readonly inAppHint: string | null
  /** Verbatim navigator.userAgent so error screenshots are self-diagnosing. */
  readonly userAgent: string
}

/**
 * Build the structured "Browser not supported" fatal payload from the
 * literal probe results. The `detail` line names the exact check that
 * failed; the full diagnostics object is rendered as a table in the view
 * so the user sees every yes/no for AudioEncoder, AudioDecoder, each
 * Opus config probe, etc.
 */
function buildBrowserUnsupportedMessage(diag: AudioDiagnostics): FatalInfo {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isIos = /iPhone|iPad|iPod/.test(ua) ||
    (ua.includes('Macintosh') && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)
  const detail = summariseOutcome(diag)
  const platformHint = isIos
    ? 'On iPhone / iPad: open this link in real Safari (the blue compass), make sure iOS is updated, and check that Lockdown Mode is off (Settings -> Privacy & Security -> Lockdown Mode). Chrome / Firefox / Edge / Brave iOS share the same limit because they all run on Apple\'s WebKit. If real Safari still says "not supported," the desktop client (Windows / macOS / Linux) is the reliable alternative.'
    : 'On desktop: update Safari to 18.4 or later, or switch to a recent Chrome, Edge, or Firefox. The desktop client (Windows / macOS / Linux) works without these constraints.'
  return {
    detail,
    diagnostics: diag,
    platformHint,
    inAppHint: detectInAppBrowserHint(ua, diag),
    userAgent: ua,
  }
}

/**
 * Render the AudioDiagnostics report as a compact, monospaced table
 * so the user (and us, via screenshot) can read literally which API was
 * present and which probe came back unsupported. No interpretation in
 * here - just facts.
 */
function renderDiagnostics(diag: AudioDiagnostics): HTMLElement {
  const rowStyle = 'display: grid; grid-template-columns: 1fr auto; gap: 8px 16px; padding: 4px 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; border-bottom: 1px solid rgba(255,255,255,0.06);'
  const headerStyle = 'font-weight: 700; text-transform: uppercase; letter-spacing: 1px; font-size: 11px; opacity: 0.7; margin: 12px 0 6px;'
  const valueStyle = (status: string): string => {
    const color = status === 'present' || status === 'supported'
      ? '#4ade80'
      : status === 'missing' || status === 'unsupported' || status === 'threw'
        ? '#f87171'
        : '#a3a3a3'
    return `color: ${color}; font-weight: 600;`
  }

  const apiRows: Array<[string, string]> = [
    ['AudioEncoder constructor', diag.audioEncoder],
    ['AudioDecoder constructor', diag.audioDecoder],
    ['AudioContext constructor', diag.audioContext],
    ['WebAssembly (Lockdown Mode indicator)', diag.webAssembly],
  ]

  const children: Array<Node> = [
    el('div', { style: headerStyle }, 'Constructors'),
  ]
  for (const [label, status] of apiRows) {
    children.push(
      el('div', { style: rowStyle }, [
        el('span', undefined, label),
        el('span', { style: valueStyle(status) }, status),
      ]),
    )
  }

  children.push(el('div', { style: headerStyle }, 'AudioEncoder.isConfigSupported probes'))
  for (const p of diag.encoderProbes) {
    children.push(
      el('div', { style: rowStyle }, [
        el('span', undefined, p.label),
        el('span', { style: valueStyle(p.status) }, p.status + (p.error ? ` (${p.error})` : '')),
      ]),
    )
  }

  children.push(el('div', { style: headerStyle }, 'AudioDecoder.isConfigSupported probes'))
  for (const p of diag.decoderProbes) {
    children.push(
      el('div', { style: rowStyle }, [
        el('span', undefined, p.label),
        el('span', { style: valueStyle(p.status) }, p.status + (p.error ? ` (${p.error})` : '')),
      ]),
    )
  }

  // Lockdown Mode high-confidence callout: WebAssembly disabled on iOS
  // Safari is a near-certain Lockdown indicator. We say so plainly here
  // rather than burying it in the hint paragraph below.
  if (diag.webAssembly === 'missing') {
    children.push(
      el(
        'p',
        {
          style:
            'margin-top: 12px; padding: 10px 12px; background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.3); border-radius: 6px; font-size: 13px; color: #fecaca;',
        },
        'WebAssembly is also missing. On iOS Safari that almost always means Lockdown Mode is enabled, which deliberately disables WebCodecs along with WASM. Turn off Lockdown Mode for this site to use the voice client in the browser.',
      ),
    )
  }

  return el(
    'div',
    {
      style:
        'margin-top: 14px; padding: 12px 14px; background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px;',
    },
    children,
  )
}

/**
 * Build a FatalInfo from a mid-session codec error (the initial probe
 * already passed, then the encoder or decoder threw while in use). We
 * don't re-run the full probe here - the platform clearly supports the
 * API, the failure is downstream - so diagnostics stays null.
 */
function buildRuntimeAudioError(where: string, message: string): FatalInfo {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isIos = /iPhone|iPad|iPod/.test(ua) ||
    (ua.includes('Macintosh') && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)
  const platformHint = isIos
    ? 'Try reloading the page. If it recurs, the desktop client (Windows / macOS / Linux) is more resilient.'
    : 'Try reloading the page. If it recurs, capture a console log and report the error.'
  return {
    detail: `Audio ${where} failed at runtime: ${message}`,
    diagnostics: null,
    platformHint,
    inAppHint: null,
    userAgent: ua,
  }
}

/**
 * One-line summary of which check failed. Reads directly from the
 * diagnostics outcome; no guessing.
 */
function summariseOutcome(diag: AudioDiagnostics): string {
  switch (diag.outcome) {
    case 'no-encoder-api':
      return 'WebCodecs AudioEncoder constructor is missing in this browser. The voice client needs it to send your microphone.'
    case 'no-decoder-api':
      return 'WebCodecs AudioDecoder constructor is missing in this browser. The voice client needs it to play other speakers.'
    case 'no-encoder-config':
      return 'AudioEncoder is present but none of the Opus encoder configs we tried were accepted by this browser.'
    case 'no-decoder-config':
      return 'AudioDecoder is present but the Opus decoder config we tried was not accepted by this browser.'
    case 'ok':
      // Defensive - we shouldn't surface a fatal when outcome is ok.
      return 'Audio support probed successfully (unexpected fatal).'
  }
}

/**
 * iOS in-app browsers (Discord, Instagram, Messenger, etc.) embed WebKit
 * via WKWebView but don't expose the same surface as full Safari. Notably
 * WebCodecs is missing, which is exactly what this page needs. We detect
 * the most common ones by name and return a tailored "open in real Safari"
 * hint; for unknown in-app browsers a generic hint still fires when the UA
 * is iOS but doesn't carry the real-Safari "Safari/" suffix.
 *
 * Returns null when the user is in a real browser; in that case the only
 * fix is to update the OS or switch browser, which the platformHint covers.
 */
function detectInAppBrowserHint(ua: string, diag?: AudioDiagnostics): string | null {
  const isIos = /iPhone|iPad|iPod/.test(ua)
  if (!isIos) return null
  const open = 'Tap the address bar or the share button (square with an up arrow), then "Open in Safari". If that\'s not offered, copy this page\'s URL and paste it into Safari\'s address bar manually.'

  // ---- Known in-app browsers ----
  // Apps that embed a WebView. These are easy: explicit UA tokens.
  if (/\bDiscord\b/i.test(ua)) return `You're in Discord's in-app browser, which doesn't expose the audio APIs the voice client needs. ${open}`
  if (/FBAN|FBAV|FBIOS|FB_IAB/i.test(ua)) return `You're in Facebook / Messenger's in-app browser, which doesn't expose the audio APIs the voice client needs. ${open}`
  if (/Instagram/i.test(ua)) return `You're in Instagram's in-app browser, which doesn't expose the audio APIs the voice client needs. ${open}`
  if (/\bTwitter\b/i.test(ua) || /\bTwitterAndroid\b/i.test(ua)) return `You're in Twitter / X's in-app browser, which doesn't expose the audio APIs the voice client needs. ${open}`
  if (/\bLine\//i.test(ua)) return `You're in LINE's in-app browser, which doesn't expose the audio APIs the voice client needs. ${open}`
  if (/WhatsApp/i.test(ua)) return `You're in WhatsApp's in-app browser, which doesn't expose the audio APIs the voice client needs. ${open}`
  if (/\bSlack\b/i.test(ua)) return `You're in Slack's in-app browser, which doesn't expose the audio APIs the voice client needs. ${open}`

  // ---- Third-party iOS browsers ----
  // Chrome iOS, Firefox iOS, Edge iOS, etc. all run on Apple's WKWebView
  // because Apple required WebKit until iOS 17.4 (EU only) / iOS 18, and
  // even where alternative engines are now legal, none of these vendors
  // have shipped a non-WebKit build at the time of writing. Crucially,
  // WebCodecs AudioEncoder is gated behind an internal Safari-only
  // WebKit preference that WKWebView apps don't have on by default, so
  // these browsers report the constructor as missing on iOS 18.4+ even
  // though full Safari on the same phone has it.
  if (/CriOS\//.test(ua)) return `You're in Chrome on iOS, which on iOS uses Apple's WebKit and currently doesn't expose the audio APIs the voice client needs. ${open}`
  if (/FxiOS\//.test(ua)) return `You're in Firefox on iOS, which on iOS uses Apple's WebKit and currently doesn't expose the audio APIs the voice client needs. ${open}`
  if (/EdgiOS\//.test(ua)) return `You're in Edge on iOS, which on iOS uses Apple's WebKit and currently doesn't expose the audio APIs the voice client needs. ${open}`
  if (/OPiOS\//.test(ua)) return `You're in Opera on iOS, which on iOS uses Apple's WebKit and currently doesn't expose the audio APIs the voice client needs. ${open}`
  if (/DuckDuckGo\//.test(ua)) return `You're in DuckDuckGo Browser on iOS, which on iOS uses Apple's WebKit and currently doesn't expose the audio APIs the voice client needs. ${open}`
  if (/\bBrave\b/i.test(ua)) return `You're in Brave on iOS, which on iOS uses Apple's WebKit and currently doesn't expose the audio APIs the voice client needs. ${open}`

  // ---- Real Safari, still failing ----
  // We're in something that looks like full Safari (no in-app marker, no
  // third-party browser marker) on iOS, but WebCodecs isn't exposed. We
  // branch the recommended-fix order on what the diagnostics actually
  // show, instead of guessing:
  //
  //   WebAssembly: missing => Lockdown Mode is on (Lockdown disables both
  //   WASM and WebCodecs together; this is the high-confidence case).
  //
  //   WebAssembly: present, both encoder + decoder constructors missing
  //   => almost certainly Private Browsing OR the WebCodecs feature flag
  //   in Safari is off OR a content blocker / configuration profile is
  //   stripping the API. NOT Lockdown Mode.
  //
  //   WebAssembly: present, only encoder constructor missing => same
  //   reasons as above plus the possibility that Apple hasn't shipped
  //   AudioEncoder on this user's Safari build yet.
  if (/Version\//.test(ua) && /Safari\//.test(ua)) {
    // Prefer the runtime probe result when we have it, fall back to a
    // direct re-check if the caller didn't pass one (legacy code paths).
    const wasmGone = diag !== undefined
      ? diag.webAssembly === 'missing'
      : typeof (globalThis as { WebAssembly?: unknown }).WebAssembly === 'undefined'
    if (wasmGone) {
      return 'Lockdown Mode is enabled (WebAssembly is also disabled, which is the give-away). Lockdown Mode turns off the audio APIs this voice client needs. Fix: Settings -> Privacy & Security -> Lockdown Mode -> Configure Web Browsing -> exclude this domain. Or use the desktop client (Windows / macOS / Linux), which is unaffected.'
    }
    const bothMissing = diag !== undefined
      && diag.audioEncoder === 'missing'
      && diag.audioDecoder === 'missing'
    if (bothMissing) {
      // Both constructors gone but WebAssembly is fine. Lockdown Mode is
      // ruled out. Lead with the most actionable causes for this exact
      // pattern: Private Browsing, the Safari feature flag, content
      // blockers, or a managed-device restriction.
      return 'You\'re in real Safari with WebAssembly enabled (so Lockdown Mode is ruled out), but the entire WebCodecs API is missing. Try, in order: (1) Settings -> Safari -> Advanced -> Feature Flags -> make sure "WebCodecs" is ON (this is the most common cause, confirmed fix for several testers); (2) open the link in a non-Private tab; (3) Settings -> Safari -> Extensions -> turn off any content blockers; (4) Settings -> Screen Time -> Content & Privacy Restrictions -> check that web content isn\'t restricted. If none of those help, the desktop client (Windows / macOS / Linux) is the reliable alternative.'
    }
    return 'You\'re in real Safari, but the audio encoder this client needs is not exposed even though Lockdown Mode is not on. Most likely causes: a content blocker stripping the API, Private Browsing restrictions, or your iOS Safari build not yet shipping the encoder. The desktop client (Windows / macOS / Linux) is unaffected by all of these.'
  }

  // ---- Generic WKWebView fallback ----
  // iOS UA but no "Safari/" suffix (most in-app browsers omit it) and
  // none of the third-party iOS browsers above matched. Catches less-
  // common in-app browsers (Reddit, TikTok, custom apps).
  if (!/Safari\//.test(ua)) {
    return `Looks like you opened this from inside another app's built-in browser, which on iOS doesn't expose the audio APIs the voice client needs. ${open}`
  }
  return null
}

interface Handlers {
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  pttDown: () => void
  pttUp: () => void
  toggleOpenMicMute: () => void
  setMode: (mode: VoiceMode) => void
  setNoiseGateThresholdDb: (db: number) => void
  setMasterGain: (v: number) => void
  setUserGain: (agid: string, v: number) => void
  toggleMute: (agid: string) => void
  setPttKey: (raw: string) => void
  setNoiseSuppression: (enabled: boolean) => void
  setMicDeviceId: (deviceId: string | null) => void
  setSpeakerDeviceId: (deviceId: string | null) => Promise<void>
  testBridge: () => Promise<void>
  runDiagnostics: () => Promise<void>
  downloadDiagnostics: () => void
  captureClip: () => Promise<void>
  openBugReport: () => void
  closeBugReport: () => void
  setBugField: <K extends 'category' | 'summary' | 'details' | 'contact'>(key: K, value: BugReportDraft[K]) => void
  submitBug: () => Promise<void>
  setTtsEnabled: (enabled: boolean) => void
  setTtsHotkey: (raw: string) => void
  openTts: () => void
  closeTts: () => void
  setTtsText: (value: string) => void
  speakTts: () => Promise<void>
  unlink: () => Promise<void>
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null) return false
  const tag = (target as HTMLElement).tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return false
}

function view(state: State, h: Handlers): HTMLElement {
  if (state.fatal !== null) {
    const f = state.fatal
    const cardChildren: Array<Node> = [
      el('div', { class: 'status error' }, f.detail),
    ]

    // Concrete probe results table. Shows AudioEncoder / AudioDecoder /
    // AudioContext / WebAssembly availability and per-config probe outcomes
    // so the user sees the literal yes/nos that drove the decision.
    if (f.diagnostics !== null) {
      cardChildren.push(renderDiagnostics(f.diagnostics))
    }

    if (f.inAppHint !== null) {
      cardChildren.push(
        el(
          'p',
          { style: 'margin-top: 16px; font-size: 14px; color: var(--warn, #f59e0b);' },
          f.inAppHint,
        ),
      )
    }
    cardChildren.push(
      el(
        'p',
        { style: 'margin-top: 12px; font-size: 13px;' },
        f.platformHint,
      ),
    )
    cardChildren.push(
      el(
        'p',
        { style: 'margin-top: 12px; font-size: 13px;' },
        [
          'For per-platform update steps (Settings paths, app-store links, alternative browsers): ',
          el(
            'a',
            { href: '/install#browser-requirements' },
            "What's wrong, and how do I fix it?",
          ),
        ],
      ),
    )
    cardChildren.push(
      el(
        'p',
        {
          style:
            'margin-top: 18px; font-size: 11px; opacity: 0.7; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; word-break: break-all;',
        },
        `User-Agent: ${f.userAgent}`,
      ),
    )
    return el('div', { class: 'page' }, [
      el('h1', undefined, 'Browser not supported'),
      el('div', { class: 'card' }, cardChildren),
    ])
  }

  // Three-column layout at desktop (>=1200px):
  //   [players]   [PTT + status + master]   [mic + inputs + devices + tools]
  // Tablet (900-1199): players sidebar + combined main stack.
  // Mobile (<900): everything stacks single-column.
  // The two main columns share a CSS grid (1fr 1fr) at desktop so cards in
  // each align to the same top edge. At smaller widths the grid collapses
  // and the right column flows under the left.
  const primary = el('main', { class: 'voice-main-primary' }, [
    el('div', { class: 'card' }, [
      modeToggle(state, h),
      pttView(state, h),
      connectBtn(state, h),
      el('div', { class: `status ${state.statusKind}` }, state.status),
      ...(state.micPermissionBlocked ? [micBlockedCard(state, h)] : []),
    ]),
    statsCard(state),
    masterVolumeCard(state, h),
  ])
  const secondary = el('main', { class: 'voice-main-secondary' }, [
    el('div', { class: 'col-heading' }, 'Settings'),
    micCard(state, h),
    inputOptionsCard(state, h),
    devicesCard(state, h),
    ttsCard(state, h),
    el('div', { class: 'col-heading' }, 'Tools'),
    moderationCard(state, h),
    bridgeTestCard(state, h),
    diagnosticsCard(state, h),
    bugReportCard(state, h),
    el('div', { style: 'text-align:center;margin-top:6px' }, [
      el('button', { class: 'btn btn-quiet', onclick: () => { void h.unlink() } }, 'Unlink this device'),
    ]),
  ])
  const layout = el('div', { class: 'page page-wide' }, [
    el('header', { class: 'voice-header' }, [
      el('h1', undefined, 'Voice'),
      el('p', { class: 'lede' }, `Linked as ${state.selfAgid ?? '?'} on ${getServerId() ?? '?'}.`),
    ]),
    el('div', { class: 'voice-layout' }, [
      el('aside', { class: 'voice-sidebar', 'data-scroll-key': 'players-sidebar' }, [
        playersCard(state, h),
      ]),
      primary,
      secondary,
    ]),
    // Build stamp - prove which bundle is loaded. If a tester reports a bug
    // and the stamp here doesn't match the latest deploy, the page is
    // running cached JS and they need a hard reload.
    el('div', { style: 'text-align:center; margin-top:18px; font-size:11px; color:#525252; font-family:ui-monospace,Menlo,Consolas,monospace' },
      `build ${__BUILD_ID__}`),
  ])
  if (state.tts.open) layout.appendChild(ttsModal(state, h))
  return layout
}

function modeToggle(state: State, h: Handlers): HTMLElement {
  const seg = (mode: VoiceMode, label: string): HTMLElement => el('button', {
    class: `seg ${state.mode === mode ? 'seg-active' : ''}`,
    onclick: () => h.setMode(mode),
    'aria-pressed': state.mode === mode ? 'true' : 'false',
  }, label)
  return el('div', { class: 'segmented' }, [
    seg('ptt', 'Push to talk'),
    seg('openMic', 'Open mic'),
  ])
}

function pttView(state: State, h: Handlers): HTMLElement {
  if (state.mode === 'openMic') {
    const live = state.connected && !state.openMicMuted
    return el('div', {
      class: `ptt ${state.connected ? '' : 'disabled'} ${live ? 'live' : ''}`,
      role: 'button',
      'aria-pressed': state.openMicMuted ? 'true' : 'false',
      onclick: () => h.toggleOpenMicMute(),
      oncontextmenu: (e: Event) => e.preventDefault(),
    }, !state.connected ? 'NOT CONNECTED' : state.openMicMuted ? 'MUTED' : 'OPEN MIC LIVE')
  }
  const hint = state.pttKey !== null ? `HOLD TO TALK (or key: ${state.pttKey})` : 'HOLD TO TALK'
  return el('div', {
    class: `ptt ${state.connected ? '' : 'disabled'} ${state.pttDown ? 'live' : ''}`,
    role: 'button',
    'aria-pressed': state.pttDown ? 'true' : 'false',
    onpointerdown: (e: PointerEvent) => {
      if (!state.connected) return
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      if ('vibrate' in navigator) navigator.vibrate(15)
      h.pttDown()
    },
    onpointerup: (e: PointerEvent) => {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* ignore */ }
      h.pttUp()
    },
    onpointercancel: (e: PointerEvent) => {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* ignore */ }
      h.pttUp()
    },
    oncontextmenu: (e: Event) => e.preventDefault(),
  }, state.pttDown ? 'TRANSMITTING' : state.connected ? hint : 'NOT CONNECTED')
}

/**
 * Robust "did the user deny mic access" check. Browsers expose this signal
 * in at least four shapes:
 *
 *   - Chrome / Firefox desktop: DOMException, name="NotAllowedError",
 *     message="Permission denied" / "Permission dismissed".
 *   - iOS Safari: DOMException, name="NotAllowedError".
 *   - Older Safari + a few Android Chrome wrappers: name="PermissionDeniedError"
 *     OR a plain Error with message "permission denied" (case varies).
 *   - Race: getUserMedia hangs while the user dismisses the prompt and then
 *     a different error fires (rare, but observed).
 *
 * We accept any error name match, any "permission(-| )(denied|not allowed)"
 * message regex, AND a Permissions API fallback. The Permissions API call
 * is fire-and-forget with a short timeout so a browser that doesn't
 * implement it doesn't stall the catch path.
 */
/**
 * Read the live Permissions API state for the mic. Returns 'unknown' if the
 * API isn't supported or rejects (e.g., Safari versions that don't surface
 * the 'microphone' name). Critical for telling the user WHY the popup isn't
 * appearing - if the state is "denied" they have to flip it in Chrome
 * settings, if it's "prompt" the browser should be prompting and isn't (OS
 * permission likely off), if it's "granted" everything should work and the
 * failure is something other than permission.
 */
async function readMicPermissionState(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  try {
    const perms = (navigator as { permissions?: Permissions }).permissions
    if (perms === undefined || typeof perms.query !== 'function') return 'unknown'
    const status = await perms.query({ name: 'microphone' as PermissionName })
    if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
      return status.state
    }
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

async function detectPermissionDenied(err: unknown): Promise<boolean> {
  // Extract name/message defensively. Don't gate on `instanceof Error` because
  // some Chrome / Android wrappers have shipped errors that look like Error
  // ducks but fail the prototype check (cross-realm rejections, structured-
  // clone round trips). Walk the object directly.
  const errObj = err as { name?: unknown; message?: unknown } | null | undefined
  const name = typeof errObj?.name === 'string' ? errObj.name : ''
  const message = typeof errObj?.message === 'string' ? errObj.message : String(err)

  // Name match. AbortError lands here too because Chrome / Firefox throw it
  // when the user dismisses the prompt without picking (browser treats as
  // denial for subsequent calls).
  if (
    name === 'NotAllowedError'
    || name === 'PermissionDeniedError'
    || name === 'SecurityError'
    || name === 'AbortError'
  ) return true

  // Message regex. Loose intentionally: anything containing "permission"
  // (case-insensitive) is treated as a permission issue because there's no
  // realistic "permission" message that isn't about the user denying access.
  if (/permission/i.test(message)) return true
  if (/(denied|dismissed|not\s*allowed|blocked).*(mic|audio|media)/i.test(message)) return true
  if (/(mic|audio|media).*(denied|dismissed|not\s*allowed|blocked)/i.test(message)) return true

  // Permissions API fallback. Chrome / Firefox / iOS 16+ support this.
  // Cast through unknown because TS lib.dom doesn't list 'microphone' in
  // PermissionName (still standardising), but the browsers accept it.
  try {
    const perms = (navigator as { permissions?: Permissions }).permissions
    if (perms !== undefined && typeof perms.query === 'function') {
      const status = await perms.query({ name: 'microphone' as PermissionName })
      if (status.state === 'denied') return true
    }
  } catch { /* Permissions API absent or rejected the 'microphone' name; fine */ }
  return false
}

function connectBtn(state: State, h: Handlers): HTMLElement {
  return state.connected
    ? el('button', { class: 'btn btn-block', onclick: () => { void h.disconnect() } }, 'Disconnect')
    : el('button', { class: 'btn btn-primary btn-block', onclick: () => { void h.connect() } }, 'Connect')
}

/**
 * Recovery card shown after any audio init failure. The behaviour shown
 * branches on the live Permissions API state (read into
 * state.micPermissionState by the catch path), because the right thing for
 * the user to do depends entirely on what the browser thinks:
 *
 *   denied   - browser has the site marked denied. JS CANNOT re-prompt;
 *              the user MUST flip it in browser/site settings.
 *   prompt   - browser would prompt but isn't, which means either
 *              (a) the user dismissed without choosing (treated as
 *              transient by some browsers) or (b) Android OS-level
 *              permission for Chrome itself is off. Show both paths.
 *   granted  - browser thinks we have permission but getUserMedia
 *              failed anyway. Almost always an OS-level / hardware
 *              issue, not a browser permission issue.
 *   unknown  - Permissions API didn't answer (Safari sometimes).
 *              Fall back to the generic per-platform steps.
 */
function micBlockedCard(state: State, h: Handlers): HTMLElement {
  const ua = navigator.userAgent
  const isIosSafari = /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS/.test(ua)
  const isIpadOs = /Macintosh/.test(ua) && 'ontouchend' in document
  const isApple = isIosSafari || isIpadOs
  const isAndroid = /Android/.test(ua)

  const stateBadge = el('div', { class: 'mic-state-badge mic-state-' + state.micPermissionState },
    `browser permission: ${state.micPermissionState}`)

  let title = 'Microphone access blocked'
  let body = ''
  let steps: string[]

  if (state.micPermissionState === 'denied') {
    title = 'Microphone access is denied'
    body = isAndroid
      ? 'The browser reports microphone as denied. This is usually one of three things; check them in order, since the first is by far the most common when no popup has ever appeared.'
      : isApple
        ? 'Safari is silently denying microphone access. It will not show a popup until you change this manually.'
        : 'Your browser is set to block microphone access. Most common cause is that the OS has revoked the browser\'s mic permission, or the browser\'s global "ask for mic" toggle is off.'
    steps = isApple
      ? [
          'In Safari, tap aA at the LEFT of the address bar.',
          'Tap Website Settings.',
          'Set Microphone to Ask or Allow.',
          'Come back to this tab and tap Retry below.',
        ]
      : isAndroid
        ? [
            '1) MOST LIKELY: Android Settings → Apps → Chrome → Permissions → Microphone → Allow. Then close this tab fully and reopen it.',
            '2) If that\'s already on: Chrome ⋮ → Settings → Site settings → Microphone. Make sure the "Sites can ask to use your microphone" toggle at the top is ON.',
            '3) Only if 1+2 are fine: scroll to the Blocked list on the same screen and reset this site\'s entry. Then fully reload.',
            'Tap Retry below after each change.',
          ]
        : [
            'Check the OS audio settings: the operating system may have revoked the browser\'s microphone permission.',
            'Check the browser\'s global microphone setting (chrome://settings/content/microphone or equivalent): the master "ask" toggle must be on.',
            'Only if those are fine: click the lock icon in the address bar → reset Microphone for this site.',
            'Reload, then tap Retry below.',
          ]
  } else if (state.micPermissionState === 'prompt') {
    title = 'Browser should be asking but isn\'t'
    body = isAndroid
      ? 'The site is allowed to ask, but no popup appeared. The most likely cause on Android is that Chrome itself does not have the system-level Microphone permission.'
      : 'The site is allowed to ask, but no popup appeared. Try tapping Retry below from a fresh tap; if that still does nothing, your operating system may have blocked the browser\'s microphone access.'
    steps = isAndroid
      ? [
          'Open Settings → Apps → Chrome → Permissions.',
          'Set Microphone to Allow.',
          'Come back here and tap Retry below.',
        ]
      : isApple
        ? [
            'Open Settings → Safari → Microphone.',
            'Set this site to Ask or Allow.',
            'Come back here and tap Retry below.',
          ]
        : [
            'Check OS audio settings: your operating system may be blocking browser microphone access.',
            'Try Retry below.',
          ]
  } else if (state.micPermissionState === 'granted') {
    title = 'Permission says granted but audio failed'
    body = 'Your browser reports microphone permission is granted, so this is not a permission problem. Likely causes: no microphone connected, the input device is in use by another app, or the OS muted the input.'
    steps = [
      'Check that a microphone is connected and not muted at the OS level.',
      'Close other apps that might be using the mic (Discord, Zoom, etc.).',
      'Tap Retry below.',
    ]
  } else {
    body = 'Audio init failed. Your browser did not show a microphone popup. Follow the steps below, then retry:'
    steps = isApple
      ? [
          'In Safari, tap aA at the LEFT of the address bar.',
          'Tap Website Settings.',
          'Set Microphone to Allow.',
          'Come back here and tap Retry below.',
        ]
      : isAndroid
        ? [
            'Tap the lock icon LEFT of the address bar.',
            'Permissions → Microphone → Reset or Allow.',
            'Also check: Android Settings → Apps → Chrome → Permissions → Microphone is on.',
            'Come back here and tap Retry below.',
          ]
        : [
            'Click the lock or info icon in the address bar.',
            'Find Microphone and set it to Allow.',
            'Reload if asked, then tap Retry below.',
          ]
  }

  return el('div', { class: 'mic-blocked' }, [
    el('div', { class: 'mic-blocked-title' }, title),
    stateBadge,
    el('p', { class: 'mic-blocked-body' }, body),
    el('ol', { class: 'mic-blocked-steps' }, steps.map((s) => el('li', undefined, s))),
    el('button',
      { class: 'btn btn-primary btn-block', onclick: () => { void h.connect() } },
      'Retry microphone'),
  ])
}

function statsCard(state: State): HTMLElement {
  const voiceLine = state.connected ? 'Voice: connected' : 'Voice: not connected'
  const voiceColor = state.connected ? 'var(--good)' : 'var(--warn)'
  const pttLine = state.mode === 'openMic'
    ? `Mode: open mic${state.openMicMuted ? ' (locally muted)' : ''}`
    : `Mode: PTT${state.pttDown ? ' (transmitting)' : ''}`
  const sentLine = `Sent: ${state.stats.packetsSent.toLocaleString()} packets, ${(state.stats.bytesSent / 1024).toFixed(1)} KB`
  const kbpsLine = `${state.stats.kbpsRecent.toFixed(1)} kbps (last 1s)`
  return el('div', { class: 'card' }, [
    el('div', { class: 'row-head' }, [
      el('div', { class: 'row-title' }, 'Your status'),
      el('div', { class: 'row-value mono' }, kbpsLine),
    ]),
    el('div', { class: 'small', style: `margin-top:4px;color:${voiceColor};font-weight:600` }, voiceLine),
    el('div', { class: 'small', style: 'margin-top:4px' }, pttLine),
    el('div', { class: 'small mono', style: 'margin-top:4px' }, sentLine),
  ])
}

function masterVolumeCard(state: State, h: Handlers): HTMLElement {
  return el('div', { class: 'card' }, [
    el('div', { class: 'row-head' }, [
      el('div', { class: 'row-title' }, 'Master volume'),
      el('div', { class: 'row-value mono' }, `${Math.round(state.masterGain * 100)}%`),
    ]),
    el('input', {
      type: 'range',
      min: '0', max: String(MAX_MASTER_GAIN), step: '0.05',
      value: String(state.masterGain),
      class: 'slider',
      'data-focus-key': 'master-gain',
      oninput: (e: Event) => h.setMasterGain(Number.parseFloat((e.target as HTMLInputElement).value)),
    }),
    el('div', { class: 'small', style: 'margin-top:6px' },
      `Default ${Math.round(DEFAULT_MASTER_GAIN * 100)}%. Affects everyone at once; per-player sliders stack on top.`),
  ])
}

function micCard(state: State, h: Handlers): HTMLElement {
  const isOpen = state.mode === 'openMic'
  const peakPct = Math.min(100, Math.round(state.micPeak * 100))
  const peakDb = linearToDbfs(state.micPeak)
  const gateDb = linearToDbfs(state.noiseGateThreshold)
  const defaultDb = linearToDbfs(DEFAULT_NOISE_GATE_THRESHOLD)
  const overThreshold = state.micPeak >= state.noiseGateThreshold
  return el('div', { class: 'card' }, [
    el('div', { class: 'row-head' }, [
      el('div', { class: 'row-title' }, 'Mic & noise gate'),
      el('div', { class: 'row-value mono' }, `${peakPct}% (${peakDb.toFixed(0)} dBFS)`),
    ]),
    el('div', { class: 'meter' }, [
      el('div', { class: `meter-fill ${overThreshold ? 'open' : ''}`, style: `width:${peakPct}%` }),
      el('div', { class: 'meter-threshold', style: `left:${Math.round(state.noiseGateThreshold * 100)}%`, title: 'noise-gate threshold' }),
    ]),
    el('div', { class: 'small', style: 'margin-top:6px' },
      isOpen
        ? 'Open mic: frames below the threshold are silenced. Drag the gate so it opens on your voice but stays closed on breathing / typing / fan hum.'
        : 'PTT mode: the gate is bypassed (the button is your gate). Threshold below is saved and reused if you switch to open mic.'),
    el('div', { class: 'row-head', style: 'margin-top:14px' }, [
      el('div', { class: 'row-title' }, 'Gate threshold'),
      el('div', { class: 'row-value mono' }, `${gateDb.toFixed(0)} dBFS`),
    ]),
    el('input', {
      type: 'range',
      min: '-60', max: '0', step: '1',
      value: String(gateDb),
      class: 'slider',
      'data-focus-key': 'noise-gate',
      oninput: (e: Event) => h.setNoiseGateThresholdDb(Number.parseFloat((e.target as HTMLInputElement).value)),
    }),
    el('div', { class: 'small', style: 'margin-top:6px' },
      `Default ${defaultDb.toFixed(0)} dBFS. Slide right (toward 0) for a stricter gate, left for a more permissive one.`),
  ])
}

function inputOptionsCard(state: State, h: Handlers): HTMLElement {
  return el('div', { class: 'card' }, [
    el('div', { class: 'row-title' }, 'Input options'),
    el('div', { class: 'row-head', style: 'margin-top:10px' }, [
      el('div', { class: 'small', style: 'color:var(--fg)' }, 'PTT keyboard hotkey'),
      el('div', { class: 'row-value mono' }, state.pttKey ?? 'none'),
    ]),
    el('input', {
      type: 'text',
      maxlength: '1',
      placeholder: 'A',
      value: state.pttKey ?? '',
      autocapitalize: 'characters',
      autocomplete: 'off',
      spellcheck: false,
      style: 'width:80px;text-transform:uppercase;margin-top:6px',
      'data-focus-key': 'ptt-key',
      oninput: (e: Event) => h.setPttKey((e.target as HTMLInputElement).value),
    }),
    el('div', { class: 'small', style: 'margin-top:6px' },
      'Single character A-Z or 0-9. Works while this tab has focus; clear to disable.'),
    el('label', { style: 'display:flex;align-items:center;gap:10px;margin:16px 0 0;font-size:14px;color:var(--fg)' }, [
      el('input', {
        type: 'checkbox',
        checked: state.noiseSuppression,
        onchange: (e: Event) => h.setNoiseSuppression((e.target as HTMLInputElement).checked),
        style: 'width:auto;min-height:auto;margin:0',
      }),
      'Browser noise suppression + AEC + AGC',
    ]),
    el('div', { class: 'small', style: 'margin-top:6px' },
      `Default ${DEFAULT_NOISE_SUPPRESSION ? 'on' : 'off'}. Cleans steady noise before transmit. Reconnect to apply.`),
  ])
}

function devicesCard(state: State, h: Handlers): HTMLElement {
  // data-focus-key lets mount() preserve focus across rerenders, and the
  // module-level mount defer in dom.ts holds the rerender entirely while a
  // <select> is focused (its native menu may be open on mobile). Together
  // those stop the "dropdown sometimes doesn't open" race that testers
  // reported - a poll-driven rerender mid-tap was detaching the select
  // before the OS menu could attach to it.
  const micSelect = el('select', {
    'data-focus-key': 'mic-device',
    onchange: (e: Event) => h.setMicDeviceId(toDeviceId((e.target as HTMLSelectElement).value)),
  })
  micSelect.appendChild(option('', 'System default', state.micDeviceId === null))
  for (const d of state.micDevices) {
    micSelect.appendChild(option(d.deviceId, d.label, state.micDeviceId === d.deviceId))
  }
  const outSelect = el('select', {
    'data-focus-key': 'speaker-device',
    onchange: (e: Event) => { void h.setSpeakerDeviceId(toDeviceId((e.target as HTMLSelectElement).value)) },
  })
  outSelect.appendChild(option('', 'System default', state.speakerDeviceId === null))
  for (const d of state.speakerDevices) {
    outSelect.appendChild(option(d.deviceId, d.label, state.speakerDeviceId === d.deviceId))
  }
  if (!state.outputSinkSupported) outSelect.disabled = true

  return el('div', { class: 'card' }, [
    el('div', { class: 'row-title' }, 'Audio devices'),
    el('label', { style: 'margin-top:10px' }, 'Microphone'),
    micSelect,
    el('label', { style: 'margin-top:10px' }, 'Output'),
    outSelect,
    el('div', { class: 'small', style: 'margin-top:6px' },
      state.outputSinkSupported
        ? 'Output switch applies immediately. Mic switch applies on next reconnect.'
        : 'Output routing requires Chrome or Edge. Other browsers always use the system default.'),
    state.micDevices.length === 0
      ? el('div', { class: 'small', style: 'margin-top:4px' },
          'Connect to voice once to populate device labels (browser only reveals names after mic permission).')
      : null,
  ])
}

function toDeviceId(value: string): string | null {
  return value.length === 0 ? null : value
}

function option(value: string, label: string, selected: boolean): HTMLOptionElement {
  const o = el('option', { value }, label)
  if (selected) o.selected = true
  return o
}

function ttsCard(state: State, h: Handlers): HTMLElement {
  const available = ttsAvailable()
  return el('div', { class: 'card' }, [
    el('div', { class: 'row-head' }, [
      el('div', { class: 'row-title' }, 'Local TTS preview'),
      el('div', { class: 'row-value mono' }, state.ttsEnabled && available ? 'enabled' : 'off'),
    ]),
    el('div', { class: 'small', style: 'margin:4px 0 10px' },
      'Speaks the typed text through your own speakers. Other proximity listeners do NOT hear it. Use the desktop client for cross-client TTS.'),
    el('label', { style: 'display:flex;align-items:center;gap:10px;margin:6px 0 0;font-size:14px;color:var(--fg)' }, [
      el('input', {
        type: 'checkbox',
        checked: state.ttsEnabled,
        disabled: !available,
        onchange: (e: Event) => h.setTtsEnabled((e.target as HTMLInputElement).checked),
        style: 'width:auto;min-height:auto;margin:0',
      }),
      'Enable on this device',
    ]),
    el('div', { class: 'row-head', style: 'margin-top:14px' }, [
      el('div', { class: 'row-title' }, 'Summon hotkey'),
      el('div', { class: 'row-value mono' }, state.ttsHotkey ?? 'none'),
    ]),
    el('input', {
      type: 'text',
      maxlength: '1',
      placeholder: 'T',
      value: state.ttsHotkey ?? '',
      autocapitalize: 'characters',
      autocomplete: 'off',
      spellcheck: false,
      disabled: !state.ttsEnabled || !available,
      style: 'width:80px;text-transform:uppercase',
      'data-focus-key': 'tts-hotkey',
      oninput: (e: Event) => h.setTtsHotkey((e.target as HTMLInputElement).value),
    }),
    el('div', { style: 'margin-top:10px' }, [
      el('button', {
        class: 'btn',
        disabled: !state.ttsEnabled || !available,
        onclick: () => h.openTts(),
      }, 'Open TTS input'),
    ]),
    !available
      ? el('div', { class: 'status warn', style: 'margin-top:8px' },
          'Your browser does not expose a TTS engine.')
      : null,
  ])
}

function ttsModal(state: State, h: Handlers): HTMLElement {
  const ta = el('textarea', {
    rows: '4',
    placeholder: 'Type what you want to say...',
    style: 'width:100%;background:var(--bg);color:var(--fg);border:1px solid var(--border);border-radius:6px;padding:10px;font-size:16px;font-family:inherit',
    'data-focus-key': 'tts-text',
    oninput: (e: Event) => h.setTtsText((e.target as HTMLTextAreaElement).value),
    onkeydown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        void h.speakTts()
      } else if (e.key === 'Escape') {
        h.closeTts()
      }
    },
  }) as HTMLTextAreaElement
  ta.value = state.tts.text

  return el('div', {
    style: 'position:fixed;inset:0;background:rgba(0,0,0,0.65);z-index:50;display:flex;align-items:center;justify-content:center;padding:20px',
    onclick: (e: Event) => { if (e.target === e.currentTarget) h.closeTts() },
  }, [
    el('div', { class: 'card', style: 'max-width:480px;width:100%;margin:0' }, [
      el('div', { class: 'row-title' }, 'Text-to-speech'),
      el('div', { class: 'small', style: 'margin:4px 0 10px' },
        'Enter to speak. Escape to close. Plays locally only.'),
      ta,
      state.tts.lastError !== null
        ? el('div', { class: 'status error', style: 'margin-top:8px' }, state.tts.lastError)
        : null,
      el('div', { style: 'display:flex;gap:8px;margin-top:14px' }, [
        el('button', {
          class: 'btn btn-primary',
          style: 'flex:1',
          disabled: state.tts.speaking,
          onclick: () => { void h.speakTts() },
        }, state.tts.speaking ? 'Speaking...' : 'Speak'),
        el('button', { class: 'btn', onclick: () => h.closeTts(), disabled: state.tts.speaking }, 'Close'),
      ]),
    ]),
  ])
}

function moderationCard(state: State, h: Handlers): HTMLElement {
  return el('div', { class: 'card' }, [
    el('div', { class: 'row-title' }, 'Moderation clips'),
    el('div', { class: 'small', style: 'margin-top:6px' },
      'Save the last 30 seconds of voice you heard. One WAV per speaker plus a manifest, zipped. Hit this within ~30 seconds of the incident.'),
    el('div', { style: 'margin-top:12px' }, [
      el('button', {
        class: 'btn btn-primary',
        disabled: !state.connected,
        onclick: () => { void h.captureClip() },
      }, 'Capture last 30 seconds'),
    ]),
  ])
}

function bridgeTestCard(state: State, h: Handlers): HTMLElement {
  const result = state.probe
  return el('div', { class: 'card' }, [
    el('div', { class: 'row-title' }, 'Test bridge connection'),
    el('div', { class: 'small', style: 'margin-top:6px' },
      'Probes /api/health, refreshes the session token, then opens + closes a transient voice WebSocket.'),
    el('div', { style: 'margin-top:12px' }, [
      el('button', {
        class: 'btn',
        disabled: result.kind === 'running',
        onclick: () => { void h.testBridge() },
      }, result.kind === 'running' ? 'Probing...' : 'Run probe'),
    ]),
    result.kind === 'idle' ? null : el('pre', {
      class: 'mono',
      style: `margin-top:10px;padding:10px;background:var(--bg);border:1px solid var(--border);border-radius:6px;white-space:pre-wrap;color:${result.kind === 'fail' ? 'var(--bad)' : result.kind === 'ok' ? 'var(--good)' : 'var(--fg-dim)'}`,
    }, result.text),
  ])
}

function diagnosticsCard(state: State, h: Handlers): HTMLElement {
  const d = state.diag
  const running = d.kind === 'running'

  const children: Array<Node | null> = [
    el('div', { class: 'row-title' }, 'Full diagnostics'),
    el('div', { class: 'small', style: 'margin-top:6px' },
      'Runs every check at once (HTTPS, WebCodecs/Opus, microphone, HTTP API, session, voice WebSocket, live roster) and writes a log you can download and send to support.'),
    el('div', { style: 'display:flex;gap:8px;margin-top:12px;flex-wrap:wrap' }, [
      el('button', {
        class: 'btn btn-primary',
        disabled: running,
        onclick: () => { void h.runDiagnostics() },
      }, running ? 'Running...' : 'Run diagnostics'),
      d.kind === 'done'
        ? el('button', { class: 'btn', onclick: () => h.downloadDiagnostics() }, 'Download .log')
        : null,
    ]),
  ]

  if (d.kind === 'done') {
    const dotColor = (s: DiagReport['checks'][number]['status']): string =>
      s === 'ok' ? 'var(--good)' : s === 'fail' ? 'var(--bad)' : s === 'warn' ? '#fbbf24' : 'var(--fg-dim)'
    const glyph = (s: DiagReport['checks'][number]['status']): string =>
      s === 'ok' ? 'OK' : s === 'fail' ? '✗' : s === 'warn' ? '!' : '–'

    const rows = d.report.checks.map((c) =>
      el('div', { style: 'padding:6px 0;border-bottom:1px solid var(--border)' }, [
        el('div', { style: 'display:flex;gap:8px;align-items:baseline' }, [
          el('span', { class: 'mono', style: `color:${dotColor(c.status)};font-weight:700;min-width:18px` }, glyph(c.status)),
          el('span', { style: 'font-weight:600' }, c.title),
        ]),
        el('div', { class: 'small', style: 'margin-left:26px;color:var(--fg-dim)' }, c.detail),
      ]),
    )

    children.push(
      el('div', {
        style: 'margin-top:12px;padding:10px;background:var(--bg);border:1px solid var(--border);border-radius:6px',
      }, [
        ...rows,
        el('div', {
          style: 'margin-top:10px;padding-top:10px;font-weight:600',
        }, `Verdict: ${d.report.verdict}`),
      ]),
    )
  }

  return el('div', { class: 'card' }, children.filter((c): c is Node => c !== null))
}

function bugReportCard(state: State, h: Handlers): HTMLElement {
  if (!state.bug.open) {
    return el('div', { class: 'card' }, [
      el('div', { class: 'row-title' }, 'Report a bug'),
      el('div', { class: 'small', style: 'margin-top:6px' },
        'Sends an authenticated report to the bridge. We only attach what you type plus your link ID + region.'),
      el('div', { style: 'margin-top:12px' }, [
        el('button', { class: 'btn', onclick: () => h.openBugReport() }, 'Open report form'),
      ]),
    ])
  }

  const status = state.bug.status

  const summaryInput = el('input', {
    type: 'text',
    value: state.bug.summary,
    placeholder: 'One-line summary',
    autocomplete: 'off',
    'data-focus-key': 'bug-summary',
    oninput: (e: Event) => h.setBugField('summary', (e.target as HTMLInputElement).value),
  })
  const detailsInput = el('textarea', {
    rows: '5',
    placeholder: 'Steps to reproduce, what you expected, what happened.',
    style: 'width:100%;background:var(--bg);color:var(--fg);border:1px solid var(--border);border-radius:6px;padding:10px;font-size:16px;font-family:inherit',
    'data-focus-key': 'bug-details',
    oninput: (e: Event) => h.setBugField('details', (e.target as HTMLTextAreaElement).value),
  }) as HTMLTextAreaElement
  detailsInput.value = state.bug.details
  const contactInput = el('input', {
    type: 'text',
    value: state.bug.contact,
    placeholder: 'Discord tag, email, or blank',
    autocomplete: 'off',
    'data-focus-key': 'bug-contact',
    oninput: (e: Event) => h.setBugField('contact', (e.target as HTMLInputElement).value),
  })
  const categorySelect = el('select', {
    onchange: (e: Event) => h.setBugField('category', (e.target as HTMLSelectElement).value as BugReportCategory),
  })
  for (const cat of ['audio', 'linking', 'ui', 'crash', 'other'] as const) {
    const o = el('option', { value: cat }, cat)
    if (state.bug.category === cat) o.selected = true
    categorySelect.appendChild(o)
  }

  return el('div', { class: 'card' }, [
    el('div', { class: 'row-title' }, 'Report a bug'),
    el('label', undefined, 'Category'),
    categorySelect,
    el('label', undefined, 'Summary'),
    summaryInput,
    el('label', undefined, 'Details'),
    detailsInput,
    el('label', undefined, 'Contact (optional)'),
    contactInput,
    status.kind === 'submitting' ? el('div', { class: 'status', style: 'margin-top:10px' }, 'Submitting...') : null,
    status.kind === 'ok' ? el('div', { class: 'status ok', style: 'margin-top:10px' }, `Thanks. Report id #${status.id} recorded.`) : null,
    status.kind === 'fail' ? el('div', { class: 'status error', style: 'margin-top:10px' }, status.message) : null,
    el('div', { style: 'display:flex;gap:8px;margin-top:14px' }, [
      el('button', {
        class: 'btn btn-primary',
        style: 'flex:1',
        disabled: status.kind === 'submitting',
        onclick: () => { void h.submitBug() },
      }, status.kind === 'submitting' ? 'Submitting...' : 'Submit report'),
      el('button', { class: 'btn', onclick: () => h.closeBugReport(), disabled: status.kind === 'submitting' }, 'Cancel'),
    ]),
  ])
}

function playersCard(state: State, h: Handlers): HTMLElement {
  const others = state.roster.filter((r) => r.agid !== state.selfAgid)
  others.sort((a, b) => {
    const aud = (state.audibleAgids.has(b.agid) ? 1 : 0) - (state.audibleAgids.has(a.agid) ? 1 : 0)
    if (aud !== 0) return aud
    return a.name.localeCompare(b.name)
  })

  const header = el('div', { class: 'row-head' }, [
    el('div', { class: 'row-title' }, 'Players online'),
    el('div', { class: 'row-value mono' }, `${others.length}`),
  ])

  if (others.length === 0) {
    return el('div', { class: 'card' }, [
      header,
      el('div', { class: 'empty' },
        state.connected ? 'No one else on the server right now.' : 'Connect to see players online.'),
    ])
  }
  const rows = others.map((r) => playerRow(state, r, h))
  return el('div', { class: 'card' }, [header, ...rows])
}

function playerRow(state: State, r: RosterEntry, h: Handlers): HTMLElement {
  const audible = state.audibleAgids.has(r.agid)
  const talking = state.talkingAgids.has(r.agid)
  const muted = state.mutedAgids.has(r.agid)
  const gain = state.userGains[r.agid] ?? 1.0

  const headRow = el('div', { class: 'player-head' }, [
    el('span', { class: `player-dot ${talking ? 'talking' : ''} ${audible ? '' : 'distant'}`, title: talking ? 'transmitting' : audible ? 'in range' : 'out of range' }),
    el('span', { class: 'player-name' }, r.name === '' ? r.agid : r.name),
    el('span', { class: 'player-meta mono' }, r.voiceConnected ? (audible ? 'voice' : 'far') : 'no voice'),
    el('button', {
      class: `btn btn-mini ${muted ? 'muted' : ''}`,
      onclick: () => h.toggleMute(r.agid),
      disabled: !r.voiceConnected,
    }, muted ? 'Muted' : 'Mute'),
  ])

  if (!r.voiceConnected) return el('div', { class: 'player-row' }, [headRow])

  const gainRow = el('div', { class: 'player-gain' }, [
    el('input', {
      type: 'range',
      min: '0', max: String(MAX_USER_GAIN), step: '0.05',
      value: String(gain),
      class: 'slider',
      disabled: muted,
      'data-focus-key': `user-gain:${r.agid}`,
      oninput: (e: Event) => h.setUserGain(r.agid, Number.parseFloat((e.target as HTMLInputElement).value)),
    }),
    el('span', { class: 'player-gain-val mono' }, `${Math.round(gain * 100)}%`),
  ])
  return el('div', { class: `player-row${talking ? ' talking' : ''}` }, [headRow, gainRow])
}

function formatConnState(cs: ConnectionState): string {
  switch (cs.kind) {
    case 'connecting': return 'Connecting to bridge...'
    case 'connected':  return 'Connected.'
    case 'reconnecting': return `Lost connection (${cs.reason}). Retrying in ${Math.round(cs.nextDelayMs / 1000)}s (attempt ${cs.attempt}).`
    case 'stopped': return 'Disconnected.'
  }
}

/**
 * Transiently connect to the voice WS as a probe, then close. Resolves on
 * successful open; rejects on close-before-open or after a timeout.
 */
function probeWs(sessionToken: string, serverId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const wsBase = window.location.origin.replace(/^http/, 'ws')
    const url = `${wsBase}/api/voice-ws?token=${encodeURIComponent(sessionToken)}&serverId=${encodeURIComponent(serverId)}`
    const ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'
    let timeout: ReturnType<typeof setTimeout> | null = null
    timeout = setTimeout(() => {
      try { ws.close() } catch { /* ignore */ }
      reject(new Error('timed out waiting for open'))
    }, 6000)
    ws.addEventListener('open', () => {
      if (timeout !== null) clearTimeout(timeout)
      try { ws.close(1000, 'probe ok') } catch { /* ignore */ }
      resolve()
    })
    ws.addEventListener('close', (e) => {
      if (timeout !== null) clearTimeout(timeout)
      if (e.code === 1000) return // already resolved
      reject(new Error(`closed before open (code=${e.code} reason=${e.reason})`))
    })
    ws.addEventListener('error', () => {
      // close handler will deal with reject; some browsers fire error before
      // close, others omit it entirely.
    })
  })
}
